import { ExternalLink, PanelRightClose } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useSyncExternalStore } from "react";

import { AppArtifactContainer } from "@/components/apps/AppArtifactContainer";
import { MarkdownContent } from "@/components/chat/MarkdownContent";
import { useArtifactStore, type Artifact } from "@/store/artifactStore";
import { useChatPanelStore } from "@/store/chatPanelStore";

const LG_QUERY = "(min-width: 1024px)";

function useIsLg(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mq = window.matchMedia(LG_QUERY);
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    },
    () => window.matchMedia(LG_QUERY).matches,
    () => true,
  );
}

function externalUrl(artifact: Artifact): string | null {
  if (artifact.kind === "google") return artifact.embed.sourceUrl;
  if (artifact.kind === "pdf") return artifact.url;
  return null;
}

/**
 * The document side panel. Slides in from the right when a chat banner is
 * clicked, collapses any time via the header button or Escape-free click;
 * full-screen sheet below lg. One artifact at a time.
 */
export function ArtifactPanel() {
  const artifact = useArtifactStore((s) => s.artifact);
  const isOpen = useArtifactStore((s) => s.isOpen);
  const close = useArtifactStore((s) => s.close);
  const chatWidth = useChatPanelStore((s) => (s.isCollapsed ? 0 : s.width));
  const isLg = useIsLg();

  const external = artifact ? externalUrl(artifact) : null;

  return (
    <AnimatePresence>
      {isOpen && artifact && (
        <motion.aside
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
          // On lg+ the panel fills the entire space to the right of the chat
          // panel; below lg it is a full-screen sheet.
          style={isLg ? { left: chatWidth } : undefined}
          className="fixed inset-y-0 right-0 left-0 z-40 flex flex-col border-l border-black/10 bg-white shadow-[-24px_0_60px_-30px_rgba(0,0,0,0.35)] dark:border-white/10 dark:bg-neutral-950"
          aria-label={artifact.label}
        >
          {/* Header */}
          <div className="flex h-14 shrink-0 items-center gap-3 border-b border-black/8 px-4 dark:border-white/10">
            <button
              type="button"
              onClick={close}
              title="Collapse"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-black/60 transition-colors hover:bg-black/5 hover:text-black lg:h-9 lg:w-9 dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white"
            >
              <PanelRightClose className="h-4.5 w-4.5" />
            </button>
            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-black dark:text-white">
              {artifact.label}
            </span>
            {external && (
              <a
                href={external}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-black/10 px-3 py-1.5 text-xs font-semibold text-black/70 transition-colors hover:bg-black/5 dark:border-white/15 dark:text-white/70 dark:hover:bg-white/10"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open
              </a>
            )}
          </div>

          {/* Body */}
          {artifact.kind === "app" ? (
            /* Identity-only payload — the container fetches appDir → files
               via the studio hooks (mounted only while the panel is open). */
            <AppArtifactContainer appDir={artifact.appDir} compact />
          ) : artifact.kind === "markdown" ? (
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              <div className="prose prose-base max-w-none text-black/85 dark:prose-invert dark:text-white/85">
                <MarkdownContent content={artifact.content} />
              </div>
            </div>
          ) : (
            <>
              <iframe
                src={artifact.kind === "google" ? artifact.embed.embedUrl : artifact.url}
                title={artifact.label}
                loading="lazy"
                sandbox="allow-scripts allow-same-origin allow-popups"
                referrerPolicy="no-referrer"
                className="min-h-0 w-full flex-1 border-0"
              />
              {artifact.kind === "google" && (
                <p className="border-t border-black/8 px-4 py-2 text-center text-[11px] text-black/50 dark:border-white/10 dark:text-white/50">
                  Not loading? The preview only works when you're signed in to the Google account
                  with access —{" "}
                  <a
                    href={artifact.embed.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold underline underline-offset-2"
                  >
                    open in Google
                  </a>
                  .
                </p>
              )}
            </>
          )}
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
